import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  TextField,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Alert,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import SignatureField from "../../../components/SignatureField";
import EditIcon from "@mui/icons-material/Edit";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Swal from "sweetalert2";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

const pad2 = (n) => String(n).padStart(2, "0");
const isValidDateObj = (d) => d instanceof Date && !Number.isNaN(d.getTime());

const toDateObj = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return isValidDateObj(d) ? d : null;
};

const fmtDate = (val) => {
  const d = toDateObj(val);
  return d ? d.toLocaleDateString() : "";
};

const fmtUsDateTimeStrict = (val) => {
  const d = toDateObj(val);
  if (!d) return "";
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const AMPM = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const hh = pad2(h);
  const min = pad2(d.getMinutes());
  return `${mm}/${dd}/${yyyy} ${hh}:${min} ${AMPM}`;
};

const isParsableDate = (v) => {
  if (!v) return false;
  const d = new Date(v);
  return !Number.isNaN(d.getTime());
};

const toISODateOnly = (val) => {
  const d = toDateObj(val);
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  return `${yyyy}-${mm}-${dd}`;
};

const normalizeYesNo = (v) => {
  if (v === null || v === undefined) return null;
  const t = String(v).trim().toLowerCase();
  if (!t) return null;
  if (["yes", "y", "true", "1"].includes(t)) return true;
  if (["no", "n", "false", "0"].includes(t)) return false;
  return null;
};

const parseAxisVPair = (val) => {
  if (!val) return { interaction: "", rating: "" };
  const idx = val.indexOf(":");
  if (idx === -1) return { interaction: val.trim(), rating: "" };
  return {
    interaction: val.slice(0, idx).trim(),
    rating: val.slice(idx + 1).trim(),
  };
};

const useAutosizeTextArea = (value) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return ref;
};

const textareaBaseStyle = {
  width: "100%",
  borderRadius: "8px",
  padding: "10px",
  border: "1px solid #ccc",
  fontSize: "14px",
  resize: "none",
  overflow: "hidden",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  fontFamily: "inherit",
  lineHeight: 1.4,
  background: "#fff",
};

const ReadOnlyTextArea = ({ value, rows = 1, style }) => {
  const ref = useAutosizeTextArea(value ?? "");
  return (
    <textarea
      ref={ref}
      readOnly
      rows={rows}
      value={value ?? ""}
      style={{ ...textareaBaseStyle, ...style }}
    />
  );
};

// Update props to include onClose
const DiagnosticAssessmentPreview = ({ patientformID, patientFormId, data, onClose }) => {
  const [diag, setDiag] = useState({});
  const [header, setHeader] = useState({});
  
  // Signature State
  const [clientSigSrc, setClientSigSrc] = useState("");
  const sigCanvasRef = useRef(null);
  const [openClientSignatureModal, setOpenClientSignatureModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const resolvedPatientFormId = useMemo(
    () =>
      patientformID ??
      patientFormId ??
      data?.patientformID ??
      data?.patientFormId ??
      data?.id ??
      data?.Id ??
      null,
    [patientformID, patientFormId, data]
  );

  const getAuthToken = () =>
    localStorage.getItem("neuroEZ_access_token") ||
    localStorage.getItem("neuro_patientportal_auth_token") ||
    "";

  // --- Signature Handlers ---

  const openClientModal = () => setOpenClientSignatureModal(true);

  const closeClientModal = () => {
    setOpenClientSignatureModal(false);
    if (sigCanvasRef.current) sigCanvasRef.current.clear();
  };

  const handleSaveClientSignature = () => {
    if (!sigCanvasRef.current) return;
    const dataUrl = sigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png");
    setClientSigSrc(dataUrl);
    closeClientModal();
  };

  const handleDeleteClientSignature = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this signature?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
      didOpen: () => {
        const swalEl = document.querySelector(".swal2-container");
        if (swalEl) swalEl.style.zIndex = "9999";
      },
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      setClientSigSrc("");

      try {
        const token = getAuthToken();
        if (!token) throw new Error("Authentication token not found. Please log in.");

        const res = await fetch(`${API_BASE_URL}/Forms/AddPatientForm`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...header,
            Id: header.Id ?? header.id ?? resolvedPatientFormId,
            patientFormId: resolvedPatientFormId,
            clientsBase64: "",
            clientsURL: "",
          }),
        });

        if (!res.ok) throw new Error("Failed to remove signature.");

        Swal.fire({
          title: "Deleted!",
          text: "Signature has been removed.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
          didOpen: () => {
            const swalEl = document.querySelector(".swal2-container");
            if (swalEl) swalEl.style.zIndex = "9999";
          },
        });
      } catch (e) {
        Swal.fire({
          title: "Error!",
          text: e.message || "Failed to remove signature.",
          icon: "error",
          didOpen: () => {
            const swalEl = document.querySelector(".swal2-container");
            if (swalEl) swalEl.style.zIndex = "9999";
          },
        });
      }
    });
  };

  const handleSaveForm = async () => {
    if (!resolvedPatientFormId) return;
    try {
      const token = getAuthToken();
      if (!token) throw new Error("Authentication token not found. Please log in.");

      const isBase64 = clientSigSrc && clientSigSrc.toString().includes("base64");

      const payload = {
        ...header,
        Id: header.Id ?? header.id ?? resolvedPatientFormId,
        patientFormId: resolvedPatientFormId,
        clientsBase64: isBase64 ? clientSigSrc : "",
        clientsURL: isBase64 ? "" : (clientSigSrc || ""),
      };

      const res = await fetch(`${API_BASE_URL}/Forms/AddPatientForm`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save form.");

      // Success Popup Logic
      setShowSuccessAlert(true);
      
      // Auto Close Logic
      setTimeout(() => {
        setShowSuccessAlert(false);
        if (onClose) onClose();
      }, 1000);

    } catch (e) {
        Swal.fire({
            title: "Error",
            text: e.message || "Failed to save form",
            icon: "error",
            didOpen: () => {
                const swalEl = document.querySelector(".swal2-container");
                if (swalEl) swalEl.style.zIndex = "9999";
            },
        });
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessAlert(false);
    if (onClose) onClose();
  };
  // //  const handleSuccessClose = () => {
  // //   setShowSuccessAlert(false);
  // //   if (onClose) {
  // //     onClose();
  //   } else {
  //     // Fallback if onClose is not provided
  //     try {
  //       const backdrop = document.querySelector(".MuiBackdrop-root");
  //       if (backdrop) backdrop.click();
  //     } catch (e) {}
  //   }
  // };

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const run = async () => {
      if (!resolvedPatientFormId) {
        setError("Missing patientFormId");
        setLoading(false);
        return;
      }
      if (!API_BASE_URL) {
        setError("API base URL is not set.");
        setLoading(false);
        return;
      }

      const token = getAuthToken();
      if (!token) {
        setError("Not authenticated (missing token)");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const headersCfg = { Authorization: `Bearer ${token}` };

        const [daRes, hdrRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/Rehabilitative/getDiagnoticAssessmentById`, {
            params: { patientFormId: resolvedPatientFormId },
            headers: headersCfg,
            signal: controller.signal,
            validateStatus: (s) => (s >= 200 && s < 300) || s === 204 || s === 404,
          }),
          axios.get(`${API_BASE_URL}/Forms/GetPatientFormsbyId`, {
            params: { Id: resolvedPatientFormId },
            headers: headersCfg,
            signal: controller.signal,
            validateStatus: (s) => (s >= 200 && s < 300) || s === 204 || s === 404,
          }),
        ]);

        if (!active) return;

        const da = daRes.status === 204 || daRes.status === 404 ? {} : daRes.data || {};
        const hdr = hdrRes.status === 204 || hdrRes.status === 404 ? {} : hdrRes.data || {};

        // Your header endpoint sometimes returns array; normalize
        const hdrObj = Array.isArray(hdr) ? hdr[0] || {} : hdr;

        setDiag(da);
        setHeader(hdrObj);

        // --- Client Signature Fetch Logic (Base64 + URL) ---
        const sigBase64 =
          da?.clientsBase64 ||
          hdrObj?.clientsBase64 ||
          da?.clientSignatureBase64 ||
          da?.patientSignatureBase64 ||
          "";

        const sigURLRaw =
          da?.clientsURL ||
          hdrObj?.clientsURL ||
          da?.clientSignatureURL ||
          da?.patientSignatureURL ||
          "";

        if (sigBase64) {
          if (active) setClientSigSrc(sigBase64);
        } else if (sigURLRaw) {
          if (/^(https?:|data:|blob:)/i.test(sigURLRaw)) {
            if (active) setClientSigSrc(sigURLRaw);
          } else {
            try {
              const res = await fetch(
                `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
                  sigURLRaw
                )}`,
                { headers: headersCfg, signal: controller.signal }
              );
              if (res.ok) {
                const link = await res.text();
                if (active && link) setClientSigSrc(link);
              }
            } catch {
              /* ignore */
            }
          }
        } else {
          setClientSigSrc("");
        }

        if (!daRes.data && !hdrRes.data) {
          setError("No Diagnostic Assessment found for this patient.");
        }
      } catch (err) {
        if (!active) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load diagnostic assessment."
        );
        setDiag({});
        setHeader({});
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    return () => {
      active = false;
      controller.abort();
    };
  }, [resolvedPatientFormId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box p={4}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );
  }

  const rawAssessmentDate =
    diag?.assessmentDate || header?.assessmentDate || header?.createdDate;

  const assessmentDate =
    rawAssessmentDate && String(rawAssessmentDate).startsWith("0001-01-01")
      ? header?.createdDate || diag?.createdDate || null
      : rawAssessmentDate;

  const clientName = header?.patientName || diag?.patientName || "";

  const clientSigDate =
    diag?.clientSignatureDate ||
    header?.clientSignatureDate ||
    diag?.patientSignatureDate ||
    header?.patientSignatureDate ||
    null;

  const dob = header?.dob || diag?.dOB || header?.dOB || "";
  const fileId =
    header?.accountNo ||
    header?.fileNo ||
    header?.fileno ||
    diag?.pateintId ||
    "";

  const accountNo = header?.accountNo || "";
  const titleField =
    header?.customFormTitle || diag?.customFormTitle || header?.title || "";

  const assessmentDateLabel = fmtDate(assessmentDate);

  const isPrinted = !!header?.isPrinted;
  const isChecked = !!header?.isChecked;
  const isAuditReady = !!header?.isAuditReady;
  const formlock = !!header?.formlock;

  const age = diag?.age || "";
  const livingSituation = diag?.livingSituation || "";
  const basicNeed = diag?.basicNeeds ?? diag?.basicNeed ?? diag?.basicneed ?? "";
  const education = diag?.education || "";
  const significiant =
    diag?.relationships ?? diag?.significiant ?? diag?.significant ?? "";
  const strengths = diag?.strengths || "";
  const beleifs = diag?.beleifs ?? diag?.beliefs ?? "";
  const contextual = diag?.contextualFactors ?? diag?.contextual ?? "";
  const phisicalHealth = diag?.phisicalHealth ?? diag?.physicalHealth ?? "";
  const medication = diag?.medication ?? diag?.currentMedications ?? "";

  const DsmDiagnosis = diag?.dsM5Diagnosis || diag?.dsmDiagnosis || [];
  const SupprotiveAssement = diag?.supportiveAssessment || [];

  const preceptionCondition =
    diag?.preceptionCondition ?? diag?.perceptionCondition ?? "";
  const descriptionSymptoms = diag?.descriptionSymptoms || "";
  const referrel = diag?.referrel ?? diag?.referral ?? "";
  const history = diag?.history || "";
  const importantIncident = diag?.importantIncident || "";
  const tramaHistory = diag?.tramaHistory || "";
  const malTreatment = diag?.malTreatment || "";
  const drugHistory = diag?.drugHistory || "";
  const healthHistory = diag?.healthHistory || "";
  const familyHealthHistory = diag?.familyHealthHistory || "";
  const cultural = diag?.cultural || "";
  const communication = diag?.communication || "";

  const appearance = diag?.appearance || "";
  const build = diag?.build || "";
  const demeanor = diag?.demeanor || "";
  const eyeContact = diag?.eyeContact || "";
  const activityLevel = diag?.activityLevel || "";
  const speech = diag?.speech || "";
  const delusions = diag?.delusions || "";
  const hallucinations = diag?.hallucinations || "";
  const selfAbuse = diag?.selfAbuse || "";
  const violence = diag?.violence || "";
  const destruction = diag?.destruction || "";
  const orientation = diag?.orientation || "";
  const memoryIssues = diag?.memoryIssues || "";
  const attention = diag?.attention || "";
  const judgment = diag?.judgment || "";
  const mood = diag?.mood || "";
  const affect = diag?.affect || "";
  const behavior = diag?.behavior || "";
  const anxiety = diag?.anxiety || "";
  const intelligenceEstimate = diag?.intelligenceEstimate || "";
  const collaborateNotes = diag?.collaborateNotes || "";

  const screeningDetails = diag?.screeningDetails || "";

  const question1 = diag?.question1 || "";
  const question2 = diag?.question2 || "";
  const question3 = diag?.question3 || "";
  const question4 = diag?.question4 || "";
  const cageCheck = !!diag?.cAGE;
  const cageScore = diag?.score || "";

  const isSDQAddendum = !!diag?.addendum;
  const isCASII = !!diag?.cASII;
  const isECSII = !!diag?.eCSII;

  const riskHarm = diag?.riskHarm || "";
  const functionalStatusCASII = diag?.functionalStatusCASII || "";
  const coOccurrence = diag?.coOccurrence || "";
  const recoveryStress = diag?.recoveryStress || "";
  const recoverySupport = diag?.recoverySupport || "";
  const resiliency = diag?.resiliency || "";
  const adolescent = diag?.adolescent || "";
  const parentCaretaker = diag?.parentCaretaker || "";
  const compositeScore = diag?.compositeScore || "";
  const serviceIntensity = diag?.serviceIntensity || "";

  const degreeSafety = diag?.degreeSafety || "";
  const caregivingRelationships = diag?.caregivingRelationships || "";
  const caregivingStrengths = diag?.caregivingStrengths || "";
  const caregivingStressors = diag?.caregivingStressors || "";
  const functionalDevelopmentalStatus =
    diag?.functionalDevelopmentalStatus || diag?.functionalStatuseCSII || "";
  const impactProblems = diag?.impactProblems || "";
  const totalScoreESCII = diag?.totalScoreESCII || "";
  const sILevel = diag?.sILevel || "";
  const serviceIntensityLevel = diag?.serviceIntensityLevel || "";
  const involvement = diag?.involvement || "";
  const fit = diag?.fit || "";
  const effectiveness = diag?.effectiveness || "";

  const contributional = diag?.contributional || "";
  const clientNeedsStr = diag?.clientNeeds || "";
  const featuresRelationshipStr = diag?.featuresRelationship || "";

  const clinicalSummary = diag?.clinicalSummary || "";
  const furtherServices = diag?.furtherServices || "";
  const diagnosticHypothesis = diag?.diagnosticHypothesis || "";

  const axisICode = diag?.axisICode || "";
  const axisIDescription = diag?.axisIDescription || "";
  const axisISummary = diag?.axisISummary || "";

  const axisIIPIRRating = diag?.axisIIPIRRating || "";
  const pirSummary = diag?.pirSummary || "";
  const axisIIRelationship = diag?.axisIIRelationship || "";
  const relationshipSummary = diag?.relationshipSummary || "";
  const axisIIIDisorders = diag?.axisIIIDisorders || "";

  const axisIVStressorsStr = diag?.axisIVStressors || "";

  const axisVRegulationStr = diag?.axisVRegulation || "";
  const axisVEngagementStr = diag?.axisVEngagement || "";
  const axisVIntentionalStr = diag?.axisVIntentional || "";
  const axisVGesturesStr = diag?.axisVGestures || "";
  const axisVFeelingsStr = diag?.axisVFeelings || "";
  const axisVThinkingStr = diag?.axisVThinking || "";
  const axisVAttention = diag?.axisVAttention || "";
  const formingRelationships = diag?.formingRelationships || "";
  const intentional = diag?.intentional || "";
  const complexGestures = diag?.complexGestures || "";
  const useSymbols = diag?.useSymbols || "";
  const symbolsLogically = diag?.symbolsLogically || "";

  const supervisedClinician =
    header?.messagesupervisedClinician || header?.messageSupervisedClinician || "";
  const supervisedDate = header?.supervisedDate || "";

  const internSignature = header?.messageinternSign || "";
  const internDate = header?.internDate || "";

  const supervisorSignature = header?.messageSupervisorSign || "";
  const supervisorDate = header?.supervisorDate || "";

  const startTimeDisplay = isParsableDate(diag?.startTime)
    ? fmtUsDateTimeStrict(diag?.startTime)
    : diag?.startTime || "";
  const endTimeDisplay = isParsableDate(diag?.endTime)
    ? fmtUsDateTimeStrict(diag?.endTime)
    : diag?.endTime || "";

  const interviewDateRaw = diag?.assessmentInterviewDate || null;

  const sdqList = diag?.sdq || [];
  const comprehensiveList = diag?.comprehensiveAssessment || [];
  const contextualList = diag?.contextualAssessment || [];
  const toolsList = diag?.assessmentsTools || [];
  const measuresList = diag?.assessmentMeasures || [];

  const contributionList = contributional ? contributional.split(";") : [""];
  const clientNeedsList = clientNeedsStr ? clientNeedsStr.split(";") : [""];
  const featuresRelationshipList = featuresRelationshipStr
    ? featuresRelationshipStr.split(";")
    : [""];

  const axisIVTokens = axisIVStressorsStr
    ? axisIVStressorsStr.split(";").filter((s) => s.trim())
    : [":"];

  const axisVRegTokens = axisVRegulationStr
    ? axisVRegulationStr.split(";").filter((s) => s.trim())
    : [":"];
  const axisVEngTokens = axisVEngagementStr
    ? axisVEngagementStr.split(";").filter((s) => s.trim())
    : [":"];
  const axisVIntTokens = axisVIntentionalStr
    ? axisVIntentionalStr.split(";").filter((s) => s.trim())
    : [":"];
  const axisVGesTokens = axisVGesturesStr
    ? axisVGesturesStr.split(";").filter((s) => s.trim())
    : [":"];
  const axisVFeelTokens = axisVFeelingsStr
    ? axisVFeelingsStr.split(";").filter((s) => s.trim())
    : [":"];
  const axisVThinkTokens = axisVThinkingStr
    ? axisVThinkingStr.split(";").filter((s) => s.trim())
    : [":"];

  const cageQuestions = [question1, question2, question3, question4].map((q) =>
    normalizeYesNo(q)
  );
  const yesCount = cageQuestions.filter((v) => v === true).length;

  const buildSignedLine = (name, dateVal) => {
    const nm = (name || "").trim();
    const dt = fmtUsDateTimeStrict(dateVal);
    const hasDate = !!dt;
    if (nm && hasDate) return `Digitally signed by ${nm} on ${dt}.`;
    if (nm) return `Digitally signed by ${nm}.`;
    if (hasDate) return `Digitally signed on ${dt}.`;
    return "—";
  };

  return (
    <>
      {showSuccessAlert && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1299,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "300px",
              padding: "20px",
              backgroundColor: "#fff",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              borderRadius: "8px",
              textAlign: "center",
              zIndex: 1300,
            }}
          >
            <Typography variant="h6" style={{ marginBottom: "10px" }}>
              Form Saved
            </Typography>
            <Typography>Form saved successfully</Typography>
            <Button 
                variant="contained" 
                onClick={handleSuccessClose}
                sx={{ mt: 2 }}
            >
                OK
            </Button>
          </div>
        </>
      )}

      <Paper sx={{ p: 0, borderRadius: 3, mb: 4 }}>
        <Box
          sx={{
            position: "sticky",
            top: "-16px",
            zIndex: (t) => t.zIndex.appBar,
            bgcolor: "#fff",
            p: 4,
            pb: 0,
            mb: 3,
          }}
        >
          <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 5 }}>
            <strong>Assessment Date:</strong> {assessmentDateLabel || "—"}
          </Typography>

          <Typography
            variant="h8"
            sx={{
              position: "absolute",
              top: 16,
              left: "50%",
              transform: "translateX(-50%)",
              fontWeight: "bold",
            }}
          >
            Diagnostic Assessment
          </Typography>

          <Box sx={{ height: 28 }} />
          <Divider />
        </Box>

        <Box sx={{ px: 4 }}>
          <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2">
                <strong>File #:</strong> {accountNo || fileId || ""}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} textAlign="center">
              <Typography variant="body2">
                <strong>Client:</strong> {clientName || ""}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} textAlign="right">
              <Typography variant="body2">
                <strong>Date of Birth:</strong> {toISODateOnly(dob) || ""}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 3 }} />
        </Box>

        <Box sx={{ px: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm="auto" sx={{ mt: 2 }}>
              <TextField
                label="Custom Title"
                value={titleField || "—"}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true, sx: { fontWeight: 600, color: "#555" } }}
                sx={{
                  minWidth: { xs: "100%", sm: "400px" },
                  "& .MuiInputBase-root": {
                    height: 40,
                    backgroundColor: "#f9fafb",
                    fontSize: "0.900rem",
                    fontWeight: 500,
                    padding: "0 10px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
                  "& .MuiInputBase-input": { cursor: "default", color: "#000" },
                }}
              />
            </Grid>

            <Grid item xs={12} sm="auto" sx={{ mt: 2 }}>
              <TextField
                label="Assessment Date"
                value={toISODateOnly(assessmentDate) || "—"}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true, sx: { fontWeight: 600, color: "#555" } }}
                sx={{
                  minWidth: { xs: "100%", sm: "400px" },
                  "& .MuiInputBase-root": {
                    height: 40,
                    backgroundColor: "#f9fafb",
                    fontSize: "0.900rem",
                    fontWeight: 500,
                    padding: "0 10px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
                  "& .MuiInputBase-input": { cursor: "default", color: "#000" },
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* MAIN CONTENT */}
        <Box sx={{ px: 4, pb: 4 }}>
          {/* Top flags */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={<Checkbox checked={isPrinted} disabled />}
                label="Printed and Filed"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel control={<Checkbox checked={isChecked} disabled />} label="Inactive" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={<Checkbox checked={isAuditReady} disabled />}
                label="Audit Ready"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel control={<Checkbox checked={formlock} disabled />} label="Lock" />
            </Grid>
          </Grid>

          {/* Diagnostic Assessment header */}
          <Grid container>
            <Grid
              item
              xs={12}
              sx={{
                bgcolor: "#F0F6F8",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                p: "10px 20px",
                borderRadius: "8px",
                m: "8px 0",
              }}
            >
              <Typography
                sx={{
                  fontWeight: "bold",
                  color: "black",
                  fontSize: "18px",
                  textAlign: "left",
                }}
              >
                Diagnostic Assessment
              </Typography>
            </Grid>
          </Grid>

          {/* Client Info Grid */}
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Client Name:
              </InputLabel>
              <ReadOnlyTextArea value={clientName || ""} rows={1} style={{ width: "270px" }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Date of Birth:
              </InputLabel>
              <ReadOnlyTextArea value={toISODateOnly(dob)} rows={1} style={{ width: "270px" }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                File ID#:
              </InputLabel>
              <ReadOnlyTextArea value={accountNo || fileId || ""} rows={1} style={{ width: "270px" }} />
            </Grid>
          </Grid>

          {/* Times & interview date */}
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 14 }}>
                Start Time:
              </Typography>
              <ReadOnlyTextArea value={startTimeDisplay || ""} rows={1} style={{ width: "270px" }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 14 }}>
                End Time:
              </Typography>
              <ReadOnlyTextArea value={endTimeDisplay || ""} rows={1} style={{ width: "270px" }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 14 }}>
                Date of Assessment Interviews:
              </Typography>
              <ReadOnlyTextArea value={toISODateOnly(interviewDateRaw)} rows={1} style={{ width: "270px" }} />
            </Grid>
          </Grid>

          {/* Contributions to the Assessment */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <InputLabel sx={{ fontWeight: "bold", color: "black", fontSize: "1rem", mb: 1 }}>
              Contributions to the Assessment
            </InputLabel>

            {contributionList.map((text, idx) => (
              <Paper
                key={idx}
                elevation={3}
                sx={{
                  p: 1.5,
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
                  mb: 1.5,
                }}
              >
                <ReadOnlyTextArea value={text || ""} rows={2} />
              </Paper>
            ))}
          </Grid>

          {/* Current Life Situation */}
          <Grid container>
            <Grid
              item
              xs={12}
              sx={{
                bgcolor: "#F0F6F8",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                p: "10px 20px",
                borderRadius: "8px",
                m: "8px 0",
              }}
            >
              <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 18 }}>
                Current Life Situation
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>Age</InputLabel>
              <InputLabel>Actual stated age but can also include developmental age...</InputLabel>
              <ReadOnlyTextArea value={age || ""} rows={2} style={{ width: "400px" }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>Living situation</InputLabel>
              <InputLabel>Describe who is in the house, what type of living situation...</InputLabel>
              <ReadOnlyTextArea value={livingSituation || ""} rows={2} style={{ width: "400px" }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Basic needs and economic status
              </InputLabel>
              <InputLabel>Describe the client/family fiscal situation including whether...</InputLabel>
              <ReadOnlyTextArea value={basicNeed || ""} rows={2} style={{ width: "400px" }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>Education</InputLabel>
              <InputLabel>Describe level of education or placement in school, current.</InputLabel>
              <ReadOnlyTextArea value={education || ""} rows={2} style={{ width: "400px" }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Significant Personal Relationships
              </InputLabel>
              <InputLabel>Describe who is important in the client's life and how they...</InputLabel>
              <ReadOnlyTextArea value={significiant || ""} rows={2} style={{ width: "400px" }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Strengths and resources
              </InputLabel>
              <InputLabel>Describe strengths and resources, including the extent and.</InputLabel>
              <ReadOnlyTextArea value={strengths || ""} rows={2} style={{ width: "400px" }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>Belief systems</InputLabel>
              <InputLabel>Describe religious affiliation or spiritual belief systems are...</InputLabel>
              <ReadOnlyTextArea value={beleifs || ""} rows={2} style={{ width: "400px" }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Contextual non-personal factors
              </InputLabel>
              <InputLabel>Describe what are other factors that help create the client...</InputLabel>
              <ReadOnlyTextArea value={contextual || ""} rows={2} style={{ width: "400px" }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                General physical health and relationship to client's ..
              </InputLabel>
              <InputLabel>Describe the client's physical health symptoms/diagnosis...</InputLabel>
              <ReadOnlyTextArea value={phisicalHealth || ""} rows={2} style={{ width: "400px" }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Current medications
              </InputLabel>
              <InputLabel>Listing of the current medications -- herbal or prescription...</InputLabel>
              <ReadOnlyTextArea value={medication || ""} rows={2} style={{ width: "400px" }} />
            </Grid>
          </Grid>

          {/* Presenting Problems & Reason for Referral */}
          <Grid container>
            <Grid
              item
              xs={12}
              sx={{
                bgcolor: "#F0F6F8",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                p: "10px 20px",
                borderRadius: "8px",
                m: "8px 0",
              }}
            >
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 18 }}>
                  Presenting Problems & Reason for Referral
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Perception of condition
                </InputLabel>
                <InputLabel>How the client sees the situation in comparison with others...</InputLabel>
                <ReadOnlyTextArea value={preceptionCondition || ""} rows={2} style={{ width: "400px" }} />
              </Grid>

              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Description of symptoms
                </InputLabel>
                <InputLabel>Report all symptoms, not just those that match the end diagnosic</InputLabel>
                <ReadOnlyTextArea value={descriptionSymptoms || ""} rows={2} style={{ width: "400px" }} />
              </Grid>

              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Reason for referral
                </InputLabel>
                <InputLabel>Briefly describe the main concerns or problems that led to ...</InputLabel>
                <ReadOnlyTextArea value={referrel || ""} rows={2} style={{ width: "400px" }} />
              </Grid>

              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  History of mental health and treatment
                </InputLabel>
                <InputLabel>Discuss other providers, historical diagnoses, symptoms they</InputLabel>
                <ReadOnlyTextArea value={history || ""} rows={2} style={{ width: "400px" }} />
              </Grid>

              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Important Developmental Incidents
                </InputLabel>
                <InputLabel>Developmental milestones, accidents, issues that impeded...</InputLabel>
                <ReadOnlyTextArea value={importantIncident || ""} rows={2} style={{ width: "400px" }} />
              </Grid>

              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Trauma history
                </InputLabel>
                <InputLabel>Significant physical, emotional, sexual, or traumatic experiences</InputLabel>
                <ReadOnlyTextArea value={tramaHistory || ""} rows={2} style={{ width: "400px" }} />
              </Grid>

              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Maltreatment or abuse issues
                </InputLabel>
                <InputLabel>Discuss if there has ever been child protection involvement...</InputLabel>
                <ReadOnlyTextArea value={malTreatment || ""} rows={2} style={{ width: "400px" }} />
              </Grid>

              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  History of alcohol and drug usage and treatment
                </InputLabel>
                <InputLabel>Discuss self report of use and reports by others, if available then</InputLabel>
                <ReadOnlyTextArea value={drugHistory || ""} rows={2} style={{ width: "400px" }} />
              </Grid>

              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Health history
                </InputLabel>
                <InputLabel>All history should be mentioned that impacts client's genetic...</InputLabel>
                <ReadOnlyTextArea value={healthHistory || ""} rows={2} style={{ width: "400px" }} />
              </Grid>

              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Family health history
                </InputLabel>
                <InputLabel>Significant physical, mental, or genetic conditions in close family</InputLabel>
                <ReadOnlyTextArea value={familyHealthHistory || ""} rows={2} style={{ width: "400px" }} />
              </Grid>

              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Cultural influences and their impact on the client
                </InputLabel>
                <InputLabel>Discuss issues of race, class, socio-economic and geographic</InputLabel>
                <ReadOnlyTextArea value={cultural || ""} rows={2} style={{ width: "400px" }} />
              </Grid>

              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Communication style
                </InputLabel>
                <InputLabel>Typical ways the client expresses themself and interacts with ...</InputLabel>
                <ReadOnlyTextArea value={communication || ""} rows={2} style={{ width: "400px" }} />
              </Grid>
            </Grid>
          </Grid>

          {/* Mental Status Exam */}
          <Grid container>
            <Grid
              item
              xs={12}
              sx={{
                bgcolor: "#F0F6F8",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                p: "10px 20px",
                borderRadius: "8px",
                m: "8px 0",
              }}
            >
              <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 18 }}>
                Mental Status Exam
              </Typography>
            </Grid>
          </Grid>

          <Typography sx={{ fontSize: 14, fontWeight: "bold", color: "black", mt: 1 }}>
            General Observations
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Appearance
              </InputLabel>
              <ReadOnlyTextArea value={appearance || ""} rows={2} style={{ width: "270px" }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Build
              </InputLabel>
              <ReadOnlyTextArea value={build || ""} rows={2} style={{ width: "270px" }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Demeanor/response
              </InputLabel>
              <ReadOnlyTextArea value={demeanor || ""} rows={2} style={{ width: "270px" }} />
            </Grid>

            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Eye contact
                </InputLabel>
                <ReadOnlyTextArea value={eyeContact || ""} rows={2} style={{ width: "270px" }} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Activity level
                </InputLabel>
                <ReadOnlyTextArea value={activityLevel || ""} rows={2} style={{ width: "270px" }} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Speech
                </InputLabel>
                <ReadOnlyTextArea value={speech || ""} rows={2} style={{ width: "270px" }} />
              </Grid>
            </Grid>
          </Grid>

          <Typography sx={{ fontSize: 14, fontWeight: "bold", color: "black", mt: 2 }}>
            Thought Content and Perception
          </Typography>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {[
              ["Delusions", delusions],
              ["Hallucinations", hallucinations],
            ].map(([label, value]) => (
              <Grid item xs={12} sm={6} md={4} key={label}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  {label}
                </InputLabel>
                <ReadOnlyTextArea value={value || ""} rows={2} style={{ width: "410px" }} />
              </Grid>
            ))}
          </Grid>

          <Typography sx={{ fontSize: 14, fontWeight: "bold", color: "black", mt: 2 }}>
            Self-Danger
          </Typography>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {[
              ["Self-Abuse", selfAbuse],
              ["Violence to Others", violence],
              ["Destruction to Property", destruction],
            ].map(([label, value]) => (
              <Grid item xs={12} sm={6} md={4} key={label}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  {label}
                </InputLabel>
                <ReadOnlyTextArea value={value || ""} rows={2} style={{ width: "270px" }} />
              </Grid>
            ))}
          </Grid>

          <Typography sx={{ fontSize: 14, fontWeight: "bold", color: "black", mt: 2 }}>
            Cognition
          </Typography>

          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Orientation
              </InputLabel>
              <ReadOnlyTextArea value={orientation || ""} rows={2} style={{ width: "200px" }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Memory Issues
              </InputLabel>
              <ReadOnlyTextArea value={memoryIssues || ""} rows={2} style={{ width: "200px" }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Attention
              </InputLabel>
              <ReadOnlyTextArea value={attention || ""} rows={2} style={{ width: "200px" }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Judgment/Insight
              </InputLabel>
              <ReadOnlyTextArea value={judgment || ""} rows={2} style={{ width: "200px" }} />
            </Grid>
          </Grid>

          <Typography sx={{ fontSize: 14, fontWeight: "bold", color: "black", mt: 2 }}>
            Thought and Perceptual Process
          </Typography>

          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Mood
              </InputLabel>
              <ReadOnlyTextArea value={mood || ""} rows={2} style={{ width: "270px" }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Affect
              </InputLabel>
              <ReadOnlyTextArea value={affect || ""} rows={2} style={{ width: "270px" }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Behavior
              </InputLabel>
              <ReadOnlyTextArea value={behavior || ""} rows={2} style={{ width: "270px" }} />
            </Grid>

            <Grid container spacing={1} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={4}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Anxiety
                </InputLabel>
                <ReadOnlyTextArea value={anxiety || ""} rows={2} style={{ width: "245px" }} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Intelligence Estimate
                </InputLabel>
                <ReadOnlyTextArea
                  value={intelligenceEstimate || ""}
                  rows={2}
                  style={{ width: "245px" }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                  Collaborative Notes (based on results above)
                </InputLabel>
                <ReadOnlyTextArea
                  value={collaborateNotes || ""}
                  rows={2}
                  style={{ width: "320px" }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Screenings */}
          <Grid container>
            <Grid
              item
              xs={12}
              sx={{
                bgcolor: "#F0F6F8",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                p: "10px 20px",
                borderRadius: "8px",
                m: "8px 0",
              }}
            >
              <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 18 }}>
                Screenings
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", fontSize: 14, mb: 0.5 }}>
                Screenings Details
              </InputLabel>
              <ReadOnlyTextArea value={screeningDetails || ""} rows={2} />
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16 }}>
                CAGE
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={cageCheck} disabled />}
                label="Check if you would like to include this screening tool"
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                Date of Screening
              </InputLabel>
              <ReadOnlyTextArea value={toISODateOnly(diag?.screeningDate)} rows={1} />
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ mb: 0.5 }}>
                1. Have you ever felt that you ought to cut down on your drinking or drug use?
              </Typography>
              <Box sx={{ display: "flex", gap: 4, ml: 3 }}>
                <label>
                  <input type="radio" disabled checked={question1 === "Yes"} style={{ marginRight: 6 }} />
                  Yes
                </label>
                <label>
                  <input type="radio" disabled checked={question1 === "No"} style={{ marginRight: 6 }} />
                  No
                </label>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ mb: 0.5 }}>
                2. Have people annoyed you by criticizing your drinking or drug use?
              </Typography>
              <Box sx={{ display: "flex", gap: 4, ml: 3 }}>
                <label>
                  <input type="radio" disabled checked={question2 === "Yes"} style={{ marginRight: 6 }} />
                  Yes
                </label>
                <label>
                  <input type="radio" disabled checked={question2 === "No"} style={{ marginRight: 6 }} />
                  No
                </label>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ mb: 0.5 }}>
                3. Have you ever felt bad or guilty about your drinking or drug use?
              </Typography>
              <Box sx={{ display: "flex", gap: 4, ml: 3 }}>
                <label>
                  <input type="radio" disabled checked={question3 === "Yes"} style={{ marginRight: 6 }} />
                  Yes
                </label>
                <label>
                  <input type="radio" disabled checked={question3 === "No"} style={{ marginRight: 6 }} />
                  No
                </label>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ mb: 0.5 }}>
                4. Have you ever had a drink or used drugs first thing in the morning to steady your nerves or to get rid of a hangover?
              </Typography>
              <Box sx={{ display: "flex", gap: 4, ml: 3 }}>
                <label>
                  <input type="radio" disabled checked={question4 === "Yes"} style={{ marginRight: 6 }} />
                  Yes
                </label>
                <label>
                  <input type="radio" disabled checked={question4 === "No"} style={{ marginRight: 6 }} />
                  No
                </label>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: "bold", color: "black" }}>Scoring</Typography>
            </Grid>

            <Grid item xs={12}>
              <InputLabel sx={{ mb: 0.5 }}>SCORE: Number of "Yes" Answers</InputLabel>
              <ReadOnlyTextArea value={String(cageScore || yesCount || 0)} rows={1} />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={!!diag?.brief} disabled />}
                label='Screened positive = a score of 1 or greater'
              />
            </Grid>
          </Grid>

          {/* Functional Assessments header */}
          <Grid container>
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  bgcolor: "#F3F7FA",
                  borderColor: "divider",
                  boxShadow: "0 2px 10px rgba(16, 24, 40, 0.06)",
                  p: { xs: "12px 14px", sm: "14px 18px" },
                  borderRadius: "10px",
                  mb: 1.5,
                }}
              >
                <Typography sx={{ fontWeight: 800, color: "#000", fontSize: 18 }}>
                  Functional Assessments
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography sx={{ color: "text.secondary", lineHeight: 1.6 }}>
                A baseline functional assessment was completed as part of this assessment,
                utilizing the Strengths and Difficulties Questionnaire (SDQ), for ages 3–18,
                and either the Child and Adolescent Service Intensity Instrument (CASII) for
                ages 6–18 or the Early Childhood Service Intensity Instrument (ECSII) for
                ages 0–5. The results for these instruments are provided here.
              </Typography>
            </Grid>

            <Grid item xs={12} sx={{ mt: 0.5 }}>
              <Typography sx={{ fontWeight: 800, color: "#000", fontSize: 16 }}>
                SDQ (ages 3–18)
              </Typography>
              <Divider sx={{ mt: 1 }} />
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ borderRadius: "10px", borderColor: "divider", p: { xs: 1, sm: 1.25 } }}>
                <FormControlLabel
                  control={<Checkbox checked={isSDQAddendum} disabled />}
                  label="Check to include this addendum in Assessment."
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  borderRadius: "10px",
                  borderColor: "divider",
                  boxShadow: "0 2px 14px rgba(16, 24, 40, 0.08)",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography sx={{ fontWeight: 800, color: "#000", mb: 1 }}>
                    Version completed
                  </Typography>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                    <FormControlLabel control={<Checkbox checked={!!sdqList[0]?.isSelf} disabled />} label="Self" />
                    <FormControlLabel control={<Checkbox checked={!!sdqList[0]?.isParent} disabled />} label="Parent" />
                    <FormControlLabel control={<Checkbox checked={!!sdqList[0]?.isTeacher} disabled />} label="Teacher" />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography sx={{ fontWeight: 800, color: "#000", mb: 1 }}>Scores</Typography>

                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={3}>
                      <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Total Score</InputLabel>
                      <ReadOnlyTextArea value={sdqList[0]?.totalScore ?? ""} rows={1} style={{ width: "190px" }} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Emotional Problems</InputLabel>
                      <ReadOnlyTextArea value={sdqList[0]?.emotional ?? ""} rows={1} style={{ width: "190px" }} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Conduct Problems</InputLabel>
                      <ReadOnlyTextArea value={sdqList[0]?.conduct ?? ""} rows={1} style={{ width: "190px" }} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Inattention Problems</InputLabel>
                      <ReadOnlyTextArea value={sdqList[0]?.inattention ?? ""} rows={1} style={{ width: "200px" }} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Peer Problems</InputLabel>
                      <ReadOnlyTextArea value={sdqList[0]?.peer ?? ""} rows={1} style={{ width: "260px" }} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Pro‑social Behavior</InputLabel>
                      <ReadOnlyTextArea value={sdqList[0]?.socialBehavior ?? ""} rows={1} style={{ width: "260px" }} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Impact Score</InputLabel>
                      <ReadOnlyTextArea value={sdqList[0]?.impactScore ?? ""} rows={1} style={{ width: "260px" }} />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* CASII */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 800, color: "#000", fontSize: 16 }}>
              CASII (ages 6-18)
            </Typography>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: "10px",
                borderColor: "divider",
                boxShadow: "none",
                p: { xs: 1, sm: 1.25 },
                mt: 1,
                mb: 1,
              }}
            >
              <FormControlLabel
                control={<Checkbox checked={isCASII} disabled />}
                label="Check to include this addendum in Assessment."
              />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                borderRadius: "10px",
                borderColor: "divider",
                boxShadow: "0 2px 14px rgba(16, 24, 40, 0.08)",
                overflow: "hidden",
              }}
            >
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography sx={{ fontWeight: 800, color: "#000", mb: 1 }}>Scores</Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Risk of Harm</InputLabel>
                    <ReadOnlyTextArea value={riskHarm ?? ""} rows={1} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Functional Status</InputLabel>
                    <ReadOnlyTextArea value={functionalStatusCASII ?? ""} rows={1} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Co-Occurrence</InputLabel>
                    <ReadOnlyTextArea value={coOccurrence ?? ""} rows={1} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Recovery-Emotional Stress</InputLabel>
                    <ReadOnlyTextArea value={recoveryStress ?? ""} rows={1} style={{ width: "250px" }} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Recovery-Environmental Support</InputLabel>
                    <ReadOnlyTextArea value={recoverySupport ?? ""} rows={1} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Resiliency and/or Responsiveness to Services</InputLabel>
                    <ReadOnlyTextArea value={resiliency ?? ""} rows={1} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Child/Adolescent: Involvement in Services</InputLabel>
                    <ReadOnlyTextArea value={adolescent ?? ""} rows={1} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Parent/Primary Caretaker: Involvement in Services</InputLabel>
                    <ReadOnlyTextArea value={parentCaretaker ?? ""} rows={1} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Composite Score</InputLabel>
                    <ReadOnlyTextArea value={compositeScore ?? ""} rows={1} style={{ width: "180px" }} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>Level of Service Intensity Recommended</InputLabel>
                    <ReadOnlyTextArea value={serviceIntensity ?? ""} rows={1} style={{ width: "260px" }} />
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
          {/* ECSII ... (your remaining content stays exactly as-is) */}
          {/* NOTE: To keep this message within platform limits, the rest of your form UI is unchanged.
             If you need me to paste the remaining unchanged ~1000 lines as well, tell me and I will
             send it in 2-3 parts. */}



               {/* ECSII (0–5) */}
        <Grid item xs={12} sx={{ mt: 3 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16 }}>
            ECSII (0-5)
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox checked={isECSII} disabled />}
            label="Check to include this addendum in Assessment."
          />
        </Grid>

        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography sx={{ color: "black", fontWeight: "bold" }}>STEP 1:</Typography>
        </Grid>

        <Grid item xs={12} container spacing={1}>
          {/* Degree of Safety */}
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
              Degree of Safety
            </InputLabel>
            <ReadOnlyTextArea
              value={degreeSafety ?? ""}
              rows={1}
              style={{ width: "250px" }}
            />
          </Grid>

          {/* Caregiving Relationships */}
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
              Caregiving Relationships
            </InputLabel>
            <ReadOnlyTextArea
              value={caregivingRelationships ?? ""}
              rows={1}
              style={{ width: "270px" }}
            />
          </Grid>

          {/* Caregiving Environment/Strengths */}
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
              Caregiving Environment/Strengths
            </InputLabel>
            <ReadOnlyTextArea
              value={caregivingStrengths ?? ""}
              rows={1}
              style={{ width: "250px" }}
            />
          </Grid>

          {/* Caregiving Environment/Stressors */}
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
              Caregiving Environment/Stressors
            </InputLabel>
            <ReadOnlyTextArea
              value={caregivingStressors ?? ""}
              rows={1}
              style={{ width: "380px" }}
            />
          </Grid>

          {/* Functional/Developmental Status */}
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
              Functional/Developmental Status
            </InputLabel>
            <ReadOnlyTextArea
              value={functionalDevelopmentalStatus ?? ""}
              rows={1}
              style={{ width: "400px" }}
            />
          </Grid>

          {/* Impact of Medical/Develop/Emot-Bx Problems */}
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
              Impact of Medical/Develop/Emot-Bx Problems
            </InputLabel>
            <ReadOnlyTextArea
              value={impactProblems ?? ""}
              rows={1}
              style={{ width: "380px" }}
            />
          </Grid>

          {/* Total Score */}
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
              Total Score
            </InputLabel>
            <ReadOnlyTextArea
              value={totalScoreESCII ?? ""}
              rows={1}
              style={{ width: "400px" }}
            />
          </Grid>
        </Grid>


        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography sx={{ color: "black", textDecoration: "underline" }}>
            <b>STEP 2:</b>
          </Typography>
        </Grid>

        <Grid item xs={12} container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
              SI Level (initial)
            </InputLabel>
            <ReadOnlyTextArea value={sILevel ?? ""} rows={1}
              style={{ width: "270px" }} />
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography sx={{ color: "black", textDecoration: "underline" }}>
            <b>STEP 3:</b>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>Independent Criteria</Typography>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography sx={{ color: "black", textDecoration: "underline" }}>
            <b>STEP 4:</b>
          </Typography>
        </Grid>

        <Grid item xs={12} container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
              Service Intensity Level (final)
            </InputLabel>
            <ReadOnlyTextArea value={serviceIntensityLevel ?? ""} rows={1}
              style={{ width: "270px" }} />
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography sx={{ color: "black", textDecoration: "underline" }}>
            <b>STEP 5:</b>
          </Typography>
        </Grid>

        <Grid item xs={12} container spacing={1}>
          {[
            ["Involvement", involvement],
            ["Fit", fit],
            ["Effectiveness", effectiveness],
          ].map(([label, value], idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <InputLabel sx={{ fontWeight: "bold", color: "black", mb: 0.5 }}>
                {label}
              </InputLabel>
              <ReadOnlyTextArea value={value ?? ""} rows={1}
                style={{ width: "270px" }} />
            </Grid>
          ))}
        </Grid>

        {/* Assessments and Tools header */}
        <Grid container>
          <Grid
            item
            xs={12}
            sx={{
              bgcolor: "#F0F6F8",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
              p: "10px 20px",
              borderRadius: "8px",
              m: "8px 0",
            }}
          >
            <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 18 }}>
              Assessments and Tools
            </Typography>
          </Grid>
        </Grid>

        {/* Comprehensive Longitudinal Assessment */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16 }}>
            Comprehensive Longitudinal Assessment
          </Typography>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {comprehensiveList.length ? (
            comprehensiveList.map((c, index) => {
              const fsParts = (c.functionalStatus || "").split(";");
              const mhParts = (c.mentalHealth || "").split(";");
              const subParts = (c.substance || "").split(";");
              const intParts = (c.interactions || "").split(";");

              return (
                <Grid item xs={12} key={index}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 1.5,
                      borderRadius: "8px",
                      border: "1px solid #ddd",
                      boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
                      mb: 1,
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={2}>
                        <InputLabel>Time Frame</InputLabel>
                        <ReadOnlyTextArea value={c.timeFrame || ""} rows={1}
                          style={{ width: "250px" }}
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <InputLabel>Functional Status</InputLabel>
                        {fsParts.map((status, idx2) => (
                          <Box key={idx2} sx={{ mt: 0 }}>
                            <ReadOnlyTextArea value={status || ""} rows={1}
                              style={{ width: "250px" }} />
                          </Box>
                        ))}
                      </Grid>

                      <Grid item xs={3}>
                        <InputLabel>Mental Health Symptoms & Treatment</InputLabel>
                        {mhParts.map((status, idx2) => (
                          <Box key={idx2} sx={{ mt: 0 }}>
                            <ReadOnlyTextArea value={status || ""} rows={1}
                              style={{ width: "260px" }} />
                          </Box>
                        ))}
                      </Grid>

                      <Grid item xs={3}>
                        <InputLabel>Substance Use & Treatment</InputLabel>
                        {subParts.map((status, idx2) => (
                          <Box key={idx2} sx={{ mt: 0.5 }}>
                            <ReadOnlyTextArea value={status || ""} rows={1}
                              style={{ width: "380px" }} />
                          </Box>
                        ))}
                      </Grid>

                      <Grid item xs={2}>
                        <InputLabel>Interactions</InputLabel>
                        {intParts.map((status, idx2) => (
                          <Box key={idx2} sx={{ mt: 0.5 }}>
                            <ReadOnlyTextArea value={status || ""} rows={1}
                              style={{ width: "390px" }} />
                          </Box>
                        ))}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2">(No Comprehensive data.)</Typography>
            </Grid>
          )}
        </Grid>

        {/* Contextual Assessment */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16, mb: 1 }}>
            Contextual Assessment
          </Typography>

          <Paper
            elevation={3}
            sx={{
              p: 1.5,
              borderRadius: "8px",
              border: "1px solid #ddd",
              boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
              mb: 1,
            }}
          >
            {/* ================= HEADER ROWS ================= */}

            {/* Line 1: Main Categories */}
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>
                  A - Triggers
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>
                  B - Responses
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>
                  C - Consequences/Results
                </Typography>
              </Grid>
            </Grid>

            {/* Line 2: Sub-categories (External/Immediate/Delayed) */}
            <Grid container spacing={2} sx={{ mb: 1, alignItems: 'center' }}>
              <Grid item xs={3}></Grid> {/* Empty space under Triggers */}
              <Grid item xs={3}></Grid> {/* Empty space under Responses */}
              <Grid item xs={3}>
                <Box display="flex" alignItems="center">
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      backgroundColor: "black",
                      px: 0.5,
                      mr: 1,
                      fontSize: "0.75rem"
                    }}
                  >
                    EXTERNAL
                  </Typography>
                  <Typography sx={{ fontWeight: "bold", color: "black", fontSize: "0.85rem" }}>
                    IMMEDIATE
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Typography sx={{ fontWeight: "bold", color: "black", fontSize: "0.85rem" }}>
                  DELAYED
                </Typography>
              </Grid>
            </Grid>

            {/* ================= EXTERNAL DATA ROWS ================= */}
            {contextualList.map((c, index) => {
              const trigParts = (c.triggers || "").split(";");
              const respParts = (c.responses || "").split(";");
              const consImmParts = (c.consequencesImmediate || "").split(";");
              const consDelParts = (c.consequencesDellayed || "").split(";");

              return trigParts.map((_, rowIndex) => (
                <Grid container spacing={2} key={`ext-${index}-${rowIndex}`} sx={{ mb: 1 }}>
                  <Grid item xs={3}>
                    <Box sx={{ mt: 0.5 }}>
                      <ReadOnlyTextArea value={trigParts[rowIndex] || ""} rows={1} />
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ mt: 0.5 }}>
                      <ReadOnlyTextArea value={respParts[rowIndex] || ""} rows={1} />
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ mt: 0.5 }}>
                      <ReadOnlyTextArea value={consImmParts[rowIndex] || ""} rows={1} />
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ mt: 0.5 }}>
                      <ReadOnlyTextArea value={consDelParts[rowIndex] || ""} rows={1} />
                    </Box>
                  </Grid>
                </Grid>
              ));
            })}

            {/* ================= INTERNAL DATA ROWS (Appended at the end) ================= */}
            <Typography sx={{ fontWeight: "bold", color: "black", fontSize: "0.85rem", mt: 2, mb: 1 }}>
              INTERNAL
            </Typography>
            {contextualList.map((c, index) => {
              const trigParts = (c.internaltriggers || "").split(";");
              const respParts = (c.internalresponses || "").split(";");
              const immParts = (c.internalconsequencesImmediate || "").split(";");
              const delParts = (c.internalconsequencesDellayed || "").split(";");

              return trigParts.map((_, rowIndex) => (
                <Grid container spacing={2} key={`int-${index}-${rowIndex}`} sx={{ mb: 1 }}>
                  <Grid item xs={3}>
                    <Box sx={{ mt: 0.5 }}>
                      <ReadOnlyTextArea value={trigParts[rowIndex] || ""} rows={1} />
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ mt: 0.5 }}>
                      <ReadOnlyTextArea value={respParts[rowIndex] || ""} rows={1} />
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ mt: 0.5 }}>
                      <ReadOnlyTextArea value={immParts[rowIndex] || ""} rows={1} />
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ mt: 0.5 }}>
                      <ReadOnlyTextArea value={delParts[rowIndex] || ""} rows={1} />
                    </Box>
                  </Grid>
                </Grid>
              ));
            })}
          </Paper>
        </Grid>

        {/* Other Assessments & Tools */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16 }}>
            Other Assessments & Tools
          </Typography>
        </Grid>

        <Grid container spacing={1}>
          {/* <Grid item xs={2}>
            <Typography sx={{ fontWeight: "bold", color: "black" }}>Date Tested</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography sx={{ fontWeight: "bold", color: "black" }}>Instrument</Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography sx={{ fontWeight: "bold", color: "black" }}>Score</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography sx={{ fontWeight: "bold", color: "black" }}>Data Source</Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography sx={{ fontWeight: "bold", color: "black" }}>Phase</Typography>
          </Grid> */}

          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                p: 1.5,
                borderRadius: "8px",
                border: "1px solid #ddd",
                boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
                mb: 1,
              }}
            >
              {toolsList.map((t, index) => (
                <Grid container spacing={2} key={index}>
                  <Grid item xs={2}>
                    <Grid item xs={2}>
                      <Typography sx={{ fontWeight: "bold", color: "black" }}>Date Tested</Typography>
                    </Grid>
                    <ReadOnlyTextArea value={toISODateOnly(t.testedDate)} rows={1} />
                  </Grid>
                  <Grid item xs={3}>
                    <Grid item xs={3}>
                      <Typography sx={{ fontWeight: "bold", color: "black" }}>Instrument</Typography>
                    </Grid>
                    <ReadOnlyTextArea value={t.instrument || ""} rows={1} />
                  </Grid>
                  <Grid item xs={2}>
                    <Grid item xs={2}>
                      <Typography sx={{ fontWeight: "bold", color: "black" }}>Score</Typography>
                    </Grid>

                    <ReadOnlyTextArea value={t.score || ""} rows={1} />
                  </Grid>
                  <Grid item xs={3}>
                    <Grid item xs={3}>
                      <Typography sx={{ fontWeight: "bold", color: "black" }}>Data Source</Typography>
                    </Grid>

                    <ReadOnlyTextArea value={t.datasource || ""} rows={1} />
                  </Grid>
                  <Grid item xs={2}>
                    <Grid item xs={2}>
                      <Typography sx={{ fontWeight: "bold", color: "black" }}>Phase</Typography>
                    </Grid>

                    <ReadOnlyTextArea value={t.phase || ""} rows={1} />
                  </Grid>

                  <Grid item xs={2}>
                    <Grid item xs={2}>
                      <Typography sx={{ fontWeight: "bold", color: "black" }}>Detail</Typography>
                    </Grid>
                    
                
                 
                    <ReadOnlyTextArea value={t.detail || ""} rows={1}
                    style={{ width: "520px" }} 
                    />
                  </Grid>
                  </Grid>
                
              ))}
            </Paper>
          </Grid>
        </Grid>

        {/* Assessment Measures */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16 }}>
            Assessment Measures
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Paper
            elevation={3}
            sx={{
              p: 1.5,
              borderRadius: "8px",
              border: "1px solid #ddd",
              boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
              mb: 1,
            }}
          >
            {measuresList.map((m, index) => (
              <Grid container spacing={2} key={index}>
                <Grid item xs={2}>
                 
              
                <Grid item xs={4}>
                   <Typography sx={{ fontWeight: "bold", color: "black" }}>
                    Assessment/Tool
                  </Typography>
                  <ReadOnlyTextArea value={m.assessmenttool || ""} rows={1}
                  style={{ width: "270px" }}  />
                </Grid>
                </Grid>

                <Grid item xs={1.5}>
                <Grid item xs={4}>
                  <Typography sx={{ fontWeight: "bold", color: "black" }}>
                    If other:
                  </Typography>
                  <ReadOnlyTextArea value={m.other || ""} rows={2}
                   style={{ width: "200px" }}  />
                </Grid>
                </Grid>

                <Grid item xs={2}>
                <Grid item xs={10}>
                   <Typography sx={{ fontWeight: "bold", color: "black" }}>
                    Details:
                  </Typography>
                  <ReadOnlyTextArea value={m.detail || ""} rows={2}
                   style={{ width: "200px" }}  />
                </Grid>
              </Grid>
              </Grid>
            ))}
          </Paper>
        </Grid>

        {/* Client Needs */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16, mb: 1 }}>
            Client Needs
          </Typography>
        </Grid>

        <Grid item xs={12} sx={{ mb: 1 }}>
          <Typography>Assessment of client needs:</Typography>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            {clientNeedsList.map((val, index) => (
              <Grid item xs={12} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 1.5,
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
                  }}
                >
                  <ReadOnlyTextArea value={val || ""} rows={2}
                
                   />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Clinical Summary */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16 }}>
            Clinical Summary
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography>Summary:</Typography>
        </Grid>
        <Grid item xs={12}>
          <ReadOnlyTextArea value={clinicalSummary} rows={2} />
        </Grid>

        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography>Recommendations for further services that are medically necessary:</Typography>
        </Grid>
        <Grid item xs={12}>
          <ReadOnlyTextArea value={furtherServices} rows={2} />
        </Grid>

        {/* Provisional diagnostic hypothesis */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16 }}>
            Provisional diagnostic hypothesis
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography>
            A provisional diagnostic hypothesis should include aspects of the following that are known at this time: ...
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <ReadOnlyTextArea value={diagnosticHypothesis} rows={2} />
        </Grid>

        {/* ================= DIAGNOSIS SECTION ================= */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16, mb: 1 }}>
            Diagnosis
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              borderRadius: "8px",
              border: "1px solid #ddd",
              boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
              mb: 2,
            }}
          >
            {DsmDiagnosis && DsmDiagnosis.length > 0 ? (
              DsmDiagnosis.map((form, index) => (
                <Box key={index} sx={{ mb: 4, borderBottom: index !== DsmDiagnosis.length - 1 ? '1px solid #eee' : 'none', pb: 2 }}>
                  <Typography sx={{ fontWeight: "bold", color: "black", mb: 1 }}>
                    DSM 5 Diagnosis #{index + 1}
                  </Typography>

                  {/* CHECKBOXES ROW */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6} md={4}>
                      <FormControlLabel
                        control={<Checkbox checked={!!form.principalDiagnosis} disabled sx={{ '&.Mui-disabled': { color: 'black' } }} />}
                        label={<Typography sx={{ color: "black", fontSize: "14px" }}>Principal Diagnosis</Typography>}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <FormControlLabel
                        control={<Checkbox checked={!!form.provisionalDiagnosis} disabled sx={{ '&.Mui-disabled': { color: 'black' } }} />}
                        label={<Typography sx={{ color: "black", fontSize: "14px" }}>Provisional Diagnosis</Typography>}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <FormControlLabel
                        control={<Checkbox checked={!!form.diagnosis} disabled sx={{ '&.Mui-disabled': { color: 'black' } }} />}
                        label={<Typography sx={{ color: "black", fontSize: "14px" }}>Diagnosis</Typography>}
                      />
                    </Grid>
                    <Grid item xs={6} md={8}>
                      <FormControlLabel
                        control={<Checkbox checked={!!form.otherDisorders} disabled sx={{ '&.Mui-disabled': { color: 'black' } }} />}
                        label={<Typography sx={{ color: "black", fontSize: "14px" }}>Other Specified / Unspecified Disorders</Typography>}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <FormControlLabel
                        control={<Checkbox checked={!!form.medical} disabled sx={{ '&.Mui-disabled': { color: 'black' } }} />}
                        label={<Typography sx={{ color: "black", fontSize: "14px" }}>Medical</Typography>}
                      />
                    </Grid>
                  </Grid>

                  {/* YELLOW DSM 5 BOX */}
                  <Grid container spacing={2} sx={{ backgroundColor: "#fff7a8", p: 2, borderRadius: 2, mb: 2, mx: 0, width: "100%" }}>
                    <Grid item xs={12}>
                      <Typography sx={{ fontWeight: "bold", color: "#333", fontSize: "16px" }}>
                        DSM 5
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Code:</Typography>
                      <ReadOnlyTextArea value={form.dSM5 || ""} rows={1} />
                    </Grid>
                    <Grid item xs={9}>
                      <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Detail:</Typography>
                      <ReadOnlyTextArea value={form.dSM5Detail || ""} rows={2} />
                    </Grid>
                  </Grid>

                  {/* YELLOW ICD 10 BOX */}
                  <Grid container spacing={2} sx={{ backgroundColor: "#fff7a8", p: 2, borderRadius: 2, mb: 2, mx: 0, width: "100%" }}>
                    <Grid item xs={12}>
                      <Typography sx={{ fontWeight: "bold", color: "#333", fontSize: "16px" }}>
                        ICD 10
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Code:</Typography>
                      <ReadOnlyTextArea value={form.iCD10 || ""} rows={1} />
                    </Grid>
                    <Grid item xs={9}>
                      <Typography sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Detail:</Typography>
                      <ReadOnlyTextArea value={form.iCD10Detail || ""} rows={2} />
                    </Grid>
                  </Grid>

                  {/* DETAILS TEXT AREAS */}
                  <Grid container spacing={2}>
                    
                    <Grid item xs={8}>
                      <Grid item xs={4}>
                      <Typography sx={{ fontWeight: "bold", color: "black", mt: 1 }}>Specifier:</Typography>
                    
                      <ReadOnlyTextArea value={form.specifier || ""} rows={1} />
                    </Grid>
                    </Grid>

                    <Grid item xs={4}>
                      <Typography sx={{ fontWeight: "bold", color: "black", mt: 1 }}>Explain:</Typography>
                    
                    <Grid item xs={8}>
                      <ReadOnlyTextArea value={form.specifierExplain || ""} rows={1} />
                    </Grid>
                    </Grid>

                   
                    <Grid item xs={8}>
                      <Grid item xs={4}>
                      <Typography sx={{ fontWeight: "bold", color: "black", mt: 1 }}>Subtype:</Typography>
                    
                      <ReadOnlyTextArea value={form.subtype || ""} rows={1} />
                    </Grid>
                     </Grid>

                    <Grid item xs={4}>
                      <Typography sx={{ fontWeight: "bold", color: "black", mt: 1 }}>Explain:</Typography>
                    
                    <Grid item xs={8}>
                      <ReadOnlyTextArea value={form.subtypeExplain || ""} rows={1} />
                    </Grid>
                    </Grid>

                    
                    <Grid item xs={8}>
                       <Grid item xs={4}>
                      <Typography sx={{ fontWeight: "bold", color: "black", mt: 1 }}>Severity:</Typography>
                    
                      <ReadOnlyTextArea value={form.severity || ""} rows={1} />
                    </Grid>
                    </Grid>

                   
                    <Grid item xs={8}>
                       <Grid item xs={4}>
                     <Typography sx={{ fontWeight: "bold", color: "black", mt: 1 }}>Explain:</Typography>
                    
                      <ReadOnlyTextArea value={form.severityExplain || ""} rows={1}
                       style={{ width: "340px" }}  />
                    </Grid>
                    </Grid>
                    <Grid container spacing={1}>

                      
                      <Grid item xs={12}>
                        <Grid item xs={12}>
                        <Typography sx={{ fontWeight: "bold", color: "black", mt: 1 }}>Factors:</Typography>
                    
                        <ReadOnlyTextArea value={form.otherFactors || ""} rows={1} />
                      </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              ))
            ) : (
              <Typography>No Diagnosis data available.</Typography>
            )}
          </Paper>
        </Grid>


        {/* ================= SUPPORTIVE ASSESSMENT SECTION ================= */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16, mb: 1 }}>
            Supportive Assessment(s)/ Functional Assessment(s) and Screening(s)
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              borderRadius: "8px",
              border: "1px solid #ddd",
              boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
              mb: 2,
            }}
          >
            {/* Header Row */}
            <Grid container spacing={2} sx={{ mb: 1, borderBottom: "1px solid #eee", pb: 1 }}>
              {/* <Grid item xs={3}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>Date</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>Name</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>Description</Typography>
              </Grid> */}
            </Grid>

            {SupprotiveAssement && SupprotiveAssement.length > 0 ? (
              SupprotiveAssement.map((form, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Grid item xs={3}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>Date</Typography>
              

                      <ReadOnlyTextArea value={form.date || ""} rows={1} />
                    </Grid>
                    </Grid>
                    <Grid item xs={3}>
                       <Grid item xs={3}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>Name</Typography>
              
                      <ReadOnlyTextArea value={form.name || ""} rows={1} />
                    </Grid>
                    </Grid>
                    <Grid item xs={6}>
                      <Grid item xs={6}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>Description</Typography>
             
                      <ReadOnlyTextArea value={form.description || ""} rows={1} />
                    </Grid>
                     </Grid>

                    {/* Notes Row */}
                    {/* <Grid item xs={2} sx={{ mt: 0 }}>
                      <Typography sx={{ fontStyle: 'italic' }}>Notes:</Typography>
                    </Grid> */}
                    <Grid item xs={10} sx={{ mt: 0 }}>
                         <Grid item xs={6}>
                <Typography sx={{ fontWeight: "bold", color: "black" }}>Note</Typography>
                      <ReadOnlyTextArea value={form.notes || ""} rows={1} />
                    </Grid>
                    </Grid>
                  </Grid>
                  {index !== SupprotiveAssement.length - 1 && <Divider sx={{ my: 2 }} />}
                </Box>
              ))
            ) : (
              <Typography>No Supportive Assessment data available.</Typography>
            )}
          </Paper>
        </Grid>

        {/* DC (0-3R) */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16 }}>
            DC (0-3R)
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography
            sx={{
              fontWeight: "bold",
              color: "black",
              backgroundColor: "#e0b351",
              borderRadius: "8px",
              p: "6px",
            }}
          >
            For Extended (if needed)
          </Typography>
        </Grid>

        {/* Axis I */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography sx={{ fontWeight: "bold", color: "black" }}>
            Axis I: Clinical Disorders
          </Typography>
        </Grid>

        <Grid item xs={1}>
          <Typography sx={{ fontWeight: "bold", color: "black" }}>Code</Typography>
        </Grid>
        <Grid item xs={10}>
          <Typography sx={{ fontWeight: "bold", color: "black" }}>Description</Typography>
        </Grid>
        <Grid item xs={1}></Grid>

        <Grid item xs={2}>
          <ReadOnlyTextArea value={axisICode} rows={1} />
        </Grid>
        <Grid item xs={7}>
          <ReadOnlyTextArea value={axisIDescription} rows={2} />
        </Grid>
        <Grid item xs={3}>
          <ReadOnlyTextArea value={axisISummary} rows={1} />
        </Grid>

        {/* Axis II */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography sx={{ fontWeight: "bold", color: "black" }}>
            Axis II: Relationship Classification
          </Typography>
        </Grid>

        <Grid item xs={8}>
          <Typography sx={{ fontWeight: "bold", color: "black" }}>
            PIR-GAS Rating
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ fontWeight: "bold", color: "black" }}>
            Date of Rating
          </Typography>
        </Grid>

        <Grid item xs={8}>
          <ReadOnlyTextArea value={axisIIPIRRating} rows={2} />
        </Grid>
        <Grid item xs={4}>
          <ReadOnlyTextArea value={toISODateOnly(diag?.pirDate)} rows={1} />
        </Grid>

        <Grid item xs={12}>
          <Typography>Summary:</Typography>
        </Grid>
        <Grid item xs={12}>
          <ReadOnlyTextArea value={pirSummary} rows={2} />
        </Grid>

        <Grid item xs={8}>
          <Typography sx={{ fontWeight: "bold", color: "black" }}>
            Relationship Quality
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ fontWeight: "bold", color: "black" }}>
            Date of Relationship
          </Typography>
        </Grid>

        <Grid item xs={8}>
          <ReadOnlyTextArea value={axisIIRelationship} rows={2} />
        </Grid>
        <Grid item xs={4}>
          <ReadOnlyTextArea value={toISODateOnly(diag?.relationshipDate)} rows={1} />
        </Grid>

        <Grid item xs={12}>
          <Typography> Descriptive Features of the Relationship:</Typography>
        </Grid>
        {featuresRelationshipList.map((val, index) => (
          <Grid item xs={12} key={index}>
            <Paper
              elevation={3}
              sx={{
                p: 1.5,
                borderRadius: "8px",
                border: "1px solid #ddd",
                boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
                mb: 1,
              }}
            >
              <ReadOnlyTextArea value={val || ""} rows={2} />
            </Paper>
          </Grid>
        ))}

        <Grid item xs={12}>
          <InputLabel>Summary:</InputLabel>
          <ReadOnlyTextArea value={relationshipSummary} rows={2} />
        </Grid>

        {/* Axis III */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16 }}>
            Axis III: Medical & Developmental Disorders and Conditions
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <ReadOnlyTextArea value={axisIIIDisorders} rows={2} />
        </Grid>

        {/* Axis IV */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography sx={{ fontWeight: "bold", color: "black", fontSize: 16 }}>
            Axis IV: Psychosocial Stressors
          </Typography>
        </Grid>

        {axisIVTokens.map((val, index) => {
          const currentValue1 = val || ":";
          const [currentInteraction1 = "", currentRating1 = ""] = currentValue1
            .split(":")
            .map((v) => v.trim());

          return (
            <Grid container spacing={2} key={index}>
              
              

              <Grid item xs={10.5}>
                
                <ReadOnlyTextArea value={currentInteraction1} rows={2} 
                style={{ width: "400px" }}/>
              </Grid>

              
              <Grid item xs={10.5}>
                <ReadOnlyTextArea value={currentRating1} rows={2}
                style={{ width: "415px" }} />
              </Grid>

              <Grid item xs={0.5}></Grid>
            </Grid>
          );
        })}

        {/* Axis V */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: 800, color: "#000", fontSize: 16 }}>
            Axis V: Emotional and Social Functioning
          </Typography>
          <Divider sx={{ mt: 1 }} />
        </Grid>

        {/* Attention and Regulation */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              borderColor: "divider",
              boxShadow: "0 2px 14px rgba(16, 24, 40, 0.08)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography sx={{ fontWeight: 800, color: "#000", fontSize: 14, mb: 1 }}>
                Attention and Regulation
              </Typography>

              {axisVRegTokens.map((val, index) => {
                const currentValue = val || ":";
                const { interaction, rating } = parseAxisVPair(currentValue);

                return (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      borderRadius: "10px",
                      borderColor: "divider",
                      bgcolor: "#FAFAFA",
                      p: { xs: 1.25, sm: 1.5 },
                      mb: 1.25,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={5}>
                        <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                          Interaction with
                        </InputLabel>
                        <ReadOnlyTextArea value={interaction} rows={2}
                        style={{ width: "365px" }} />
                      </Grid>

                      <Grid item xs={12} md={7}>
                        <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                          Rating
                        </InputLabel>
                        <ReadOnlyTextArea value={rating} rows={2} 
                        style={{ width: "365px" }}/>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}

              <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                Notes
              </InputLabel>
              <ReadOnlyTextArea value={axisVAttention} rows={2} 
              />
            </Box>
          </Paper>
        </Grid>

        {/* Forming Relationships / Mutual Engagement */}
        <Grid item xs={12} sx={{ mt: 1.5 }}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              borderColor: "divider",
              boxShadow: "0 2px 14px rgba(16, 24, 40, 0.08)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography sx={{ fontWeight: 800, color: "#000", fontSize: 14, mb: 1 }}>
                Forming Relationships / Mutual Engagement
              </Typography>

              {axisVEngTokens.map((val, index) => {
                const currentValue = val || ":";
                const { interaction, rating } = parseAxisVPair(currentValue);

                return (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      borderRadius: "10px",
                      borderColor: "divider",
                      bgcolor: "#FAFAFA",
                      p: { xs: 1.25, sm: 1.5 },
                      mb: 1.25,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={5}>
                        <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                          Interaction with
                        </InputLabel>
                        <ReadOnlyTextArea value={interaction} rows={2} 
                        style={{ width: "365px" }}/>
                      </Grid>

                      <Grid item xs={12} md={7}>
                        <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                          Rating
                        </InputLabel>
                        <ReadOnlyTextArea value={rating} rows={2}
                        style={{ width: "365px" }} />
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}

              <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                Notes
              </InputLabel>
              <ReadOnlyTextArea value={formingRelationships} rows={2} />
            </Box>
          </Paper>
        </Grid>

        {/* Intentional two-way communication */}
        <Grid item xs={12} sx={{ mt: 1.5 }}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              borderColor: "divider",
              boxShadow: "0 2px 14px rgba(16, 24, 40, 0.08)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography sx={{ fontWeight: 800, color: "#000", fontSize: 14, mb: 1 }}>
                Intentional two-way communication
              </Typography>

              {axisVIntTokens.map((val, index) => {
                const currentValue = val || ":";
                const { interaction, rating } = parseAxisVPair(currentValue);

                return (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      borderRadius: "10px",
                      borderColor: "divider",
                      bgcolor: "#FAFAFA",
                      p: { xs: 1.25, sm: 1.5 },
                      mb: 1.25,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={5}>
                        <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                          Interaction with
                        </InputLabel>
                        <ReadOnlyTextArea value={interaction} rows={2}
                        style={{ width: "365px" }} />
                      </Grid>

                      <Grid item xs={12} md={7}>
                        <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                          Rating
                        </InputLabel>
                        <ReadOnlyTextArea value={rating} rows={2} 
                        style={{ width: "365px" }}/>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}

              <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                Notes
              </InputLabel>
              <ReadOnlyTextArea value={intentional} rows={2} />
            </Box>
          </Paper>
        </Grid>

        {/* Complex gestures and problem solving */}
        <Grid item xs={12} sx={{ mt: 1.5 }}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              borderColor: "divider",
              boxShadow: "0 2px 14px rgba(16, 24, 40, 0.08)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography sx={{ fontWeight: 800, color: "#000", fontSize: 14, mb: 1 }}>
                Complex gestures and problem solving
              </Typography>

              {axisVGesTokens.map((val, index) => {
                const currentValue = val || ":";
                const { interaction, rating } = parseAxisVPair(currentValue);

                return (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      borderRadius: "10px",
                      borderColor: "divider",
                      bgcolor: "#FAFAFA",
                      p: { xs: 1.25, sm: 1.5 },
                      mb: 1.25,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={5}>
                        <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                          Interaction with
                        </InputLabel>
                        <ReadOnlyTextArea value={interaction} rows={2}
                        style={{ width: "365px" }} />
                      </Grid>

                      <Grid item xs={12} md={7}>
                        <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                          Rating
                        </InputLabel>
                        <ReadOnlyTextArea value={rating} rows={2} 
                        style={{ width: "365px" }}/>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}

              <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                Notes
              </InputLabel>
              <ReadOnlyTextArea value={complexGestures} rows={2} />
            </Box>
          </Paper>
        </Grid>

        {/* Use of symbols to express thoughts/feelings */}
        <Grid item xs={12} sx={{ mt: 1.5 }}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              borderColor: "divider",
              boxShadow: "0 2px 14px rgba(16, 24, 40, 0.08)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography sx={{ fontWeight: 800, color: "#000", fontSize: 14, mb: 1 }}>
                Use of symbols to express thoughts/feelings
              </Typography>

              {axisVFeelTokens.map((val, index) => {
                const currentValue = val || ":";
                const { interaction, rating } = parseAxisVPair(currentValue);

                return (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      borderRadius: "10px",
                      borderColor: "divider",
                      bgcolor: "#FAFAFA",
                      p: { xs: 1.25, sm: 1.5 },
                      mb: 1.25,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={5}>
                        <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                          Interaction with
                        </InputLabel>
                        <ReadOnlyTextArea value={interaction} rows={2} 
                        style={{ width: "365px" }}/>
                      </Grid>

                      <Grid item xs={12} md={7}>
                        <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                          Rating
                        </InputLabel>
                        <ReadOnlyTextArea value={rating} rows={2} 
                        style={{ width: "365px" }}/>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}

              <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
                Notes
              </InputLabel>
              <ReadOnlyTextArea value={useSymbols} rows={2} />
            </Box>
          </Paper>
        </Grid>

        {/* Connecting symbols logically/abstract thinking */}
        <Grid item xs={12} sx={{ mt: 1.5 }}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              borderColor: "divider",
              boxShadow: "0 2px 14px rgba(16, 24, 40, 0.08)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography sx={{ fontWeight: 800, color: "black", fontSize: 14, mb: 1 }}>
                Connecting symbols logically/abstract thinking
              </Typography>

              {axisVThinkTokens.map((val, index) => {
                const currentValue = val || ":";
                const { interaction, rating } = parseAxisVPair(currentValue);

                return (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      borderRadius: "10px",
                      borderColor: "divider",
                      bgcolor: "#FAFAFA",
                      p: { xs: 1.25, sm: 1.5 },
                      mb: 1.25,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={5}>
                        <InputLabel sx={{ fontWeight: 700, color: "black", mb: 0.5, fontSize: 13 }}>
                          Interaction with
                        </InputLabel>
                        <ReadOnlyTextArea value={interaction} rows={2}
                        style={{ width: "365px" }} />
                      </Grid>

                      <Grid item xs={12} md={7}>
                        <InputLabel sx={{ fontWeight: 700, color: "black", mb: 0.5, fontSize: 13 }}>
                          Rating
                        </InputLabel>

                        <ReadOnlyTextArea value={rating} rows={2} 
                        style={{ width: "365px" }}/>

                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}
            </Box>
          </Paper>
        </Grid>
        <InputLabel sx={{ fontWeight: 700, color: "#000", mb: 0.5, fontSize: 13 }}>
          Notes
        </InputLabel>

        <Grid item xs={12}>
          <ReadOnlyTextArea value={symbolsLogically} rows={2} />
        </Grid>


          <Divider sx={{ my: 4 }} />


          {/* Signatures */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Signatures
          </Typography>

          {/* Client Signature Block (Open/Edit/Remove) */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              <strong>Client Signature:</strong>
            </Typography>

            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              {clientSigSrc ? (
                <>
                  <img
                    src={clientSigSrc}
                    alt="Client Signature"
                    style={{
                      display: "block",
                      width: 200,
                      height: 80,
                      border: "2px solid #000",
                      backgroundColor: "#f5f5f5",
                      padding: 10,
                      borderRadius: 8,
                      objectFit: "contain",
                    }}
                  />

                  <Tooltip title="Edit Signature" arrow>
                    <Button
                      onClick={openClientModal}
                      variant="outlined"
                      size="small"
                      style={{ minWidth: "32px", padding: "4px" }}
                      aria-label="Edit Signature"
                    >
                      <EditIcon />
                    </Button>
                  </Tooltip>

                  <Tooltip title="Remove Signature" arrow>
                    <Button
                      onClick={handleDeleteClientSignature}
                      size="small"
                      style={{ minWidth: "32px", padding: "4px" }}
                      aria-label="Remove Signature"
                    >
                      <RemoveCircleOutlineIcon style={{ color: "red" }} />
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <Link component="button" onClick={openClientModal}>
                  Open signing window
                </Link>
              )}
            </Box>

            <Typography sx={{ fontWeight: 600, mt: 0.75 }}>
              {buildSignedLine(clientName, clientSigDate)}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              <strong>Supervised Clinician:</strong>
            </Typography>
            <Typography sx={{ fontWeight: 600, mt: 0.5 }}>
              {buildSignedLine(supervisedClinician, supervisedDate)}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              <strong>Intern Signature:</strong>
            </Typography>
            <Typography sx={{ fontWeight: 600, mt: 0.5 }}>
              {buildSignedLine(internSignature, internDate)}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              <strong>Supervisor Signature:</strong>
            </Typography>
            <Typography sx={{ fontWeight: 600, mt: 0.5 }}>
              {buildSignedLine(supervisorSignature, supervisorDate)}
            </Typography>
          </Box>

          {/* Save Form button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSaveForm}>
              Save Form
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Signature Modal */}
      <Dialog
        open={openClientSignatureModal}
        onClose={closeClientModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #000", mt: 1 }}>
            <SignatureField
              ref={sigCanvasRef}
             
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveClientSignature}
            sx={{ minWidth: 120, mr: 2 }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => sigCanvasRef.current?.clear()}
            sx={{ minWidth: 120 }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DiagnosticAssessmentPreview;